import * as styles from "./ProjectsTab.css";

import { clsx } from "clsx";
import { useAtomValue } from "jotai";
import type { JSX } from "react";
import type { RouteName } from "routing";

import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import { url } from "@frontend/lib/http";
import { Trans, useLingui } from "@lingui/react/macro";

import { clientEditorAtom } from "../stores/editor";
import type { QuickviewProject } from "../types";
import { SectionFrame } from "./Section";

export function ProjectsTab(): JSX.Element {
  const { t } = useLingui();
  const { projects, projectsError } = useAtomValue(clientEditorAtom);

  const hasSubscriptions = 0 < projects.subscriptions.length;
  const hasInterventions = 0 < projects.interventions.length;

  return (
    <div className={styles.tab}>
      {projectsError ? (
        <p className={styles.alert} role="alert">
          {projectsError}
        </p>
      ) : null}

      {hasSubscriptions || hasInterventions ? (
        <div className={styles.grid}>
          {hasSubscriptions ? (
            <ProjectGroup
              icon="Repeat"
              title={t`Subscriptions`}
              projects={projects.subscriptions}
              route="eco_pro_project_subscription_index"
            />
          ) : null}

          {hasInterventions ? (
            <ProjectGroup
              icon="Zap"
              title={t`Interventions`}
              projects={projects.interventions}
              route="eco_pro_project_intervention_index"
            />
          ) : null}
        </div>
      ) : (
        <p className={styles.empty}>
          <Icon name="FolderOpen" size="sm" />
          <Trans>This client has no project yet.</Trans>
        </p>
      )}
    </div>
  );
}

function ProjectGroup({
  icon,
  title,
  projects,
  route,
}: {
  icon: IconName;
  title: string;
  projects: QuickviewProject[];
  route: RouteName;
}): JSX.Element {
  const { t } = useLingui();

  return (
    <SectionFrame icon={icon} legend={title}>
      <div className={styles.list}>
        {projects.map((project) => {
          const label = project.label ?? t`Untitled`;

          return (
            <a
              key={project.id}
              className={clsx(
                styles.projectLink,
                project.isArchived && styles.archived,
              )}
              href={url(route, { path: project.id })}
            >
              <Icon className={styles.projectIcon} name="ExternalLink" />
              <span className={styles.projectName}>{label}</span>
              <span className={styles.projectId}>#{project.id}</span>
            </a>
          );
        })}
      </div>
    </SectionFrame>
  );
}
